import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { Clock, Tag } from 'lucide-react';
import Badge from '../ui/Badge';
import Modal from '../ui/Modal';
import CheckboxGroup from '../ui/CheckboxGroup';
import AnnouncementMemberService from '../../services/AnnouncementMemberService';

// Mirrors src/constants/incidentRoles.js on the server.
const INCIDENT_ROLES = [
    'Incident Commander',
    'Triage Officer',
    'Treatment Lead',
    'Decon Officer',
    'Transport Coordinator',
    'Safety Officer',
    'Documentation/Scribe',
    'Liaison',
].map((role) => ({ value: role, label: role }));

const ROLE_LABELS = { admins: 'Admin', doctors: 'Doctor', nurses: 'Nurse', rsos: 'RSO' };

function initialsOf(name) {
    if (!name) return '?';
    return name.split(' ').filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join('');
}

// Roster of every responder assigned to this incident (individually or via a team),
// letting command mark arrival and, once arrived, deploy them Active or hold them
// Standby, plus assign their on-scene role(s). Distinct from ResponseSummary, which
// only tracks accept/reject of the original notification.
function ResponderRoster({ members = [], onUpdated }) {
    const [rolesFor, setRolesFor] = useState(null);
    const [pendingRoles, setPendingRoles] = useState([]);
    const [busyId, setBusyId] = useState(null);

    const markArrived = async (m) => {
        setBusyId(m.id);
        try {
            await AnnouncementMemberService.update(m.id, { ata: new Date().toISOString() });
            toast.success(`${m.member?.fullname || 'Responder'} marked arrived.`);
            onUpdated?.();
        } catch (err) {
            toast.error('Failed to mark arrived.');
        } finally {
            setBusyId(null);
        }
    };

    const setStatus = async (m, status) => {
        if (m.onSceneStatus === status) return;
        setBusyId(m.id);
        try {
            await AnnouncementMemberService.update(m.id, { onSceneStatus: status });
            toast.success(`${m.member?.fullname || 'Responder'} marked ${status}.`);
            onUpdated?.();
        } catch (err) {
            toast.error('Failed to update status.');
        } finally {
            setBusyId(null);
        }
    };

    const openRoles = (m) => {
        setRolesFor(m);
        setPendingRoles(m.roles || []);
    };

    const saveRoles = async () => {
        if (!rolesFor) return;
        try {
            await AnnouncementMemberService.updateRoles(rolesFor.id, pendingRoles);
            setRolesFor(null);
            onUpdated?.();
        } catch (err) {
            toast.error('Failed to save roles.');
        }
    };

    if (members.length === 0) {
        return (
            <div className="strategy-card text-center text-sm text-neutral-400 py-10 dark:text-neutral-500">
                No responders assigned to this incident yet.
            </div>
        );
    }

    return (
        <div className="space-y-2.5">
            {members.map((m) => {
                const arrived = !!m.ata;
                const isActive = m.onSceneStatus === 'Active';
                const busy = busyId === m.id;
                return (
                    <div
                        key={m.id}
                        className="p-3 rounded-lg border border-neutral-100 dark:border-neutral-800 flex flex-col sm:flex-row sm:items-center gap-3"
                    >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-10 h-10 rounded-full bg-seha-50 text-seha-700 border border-seha-100 flex items-center justify-center font-bold text-sm shrink-0 dark:bg-seha-500/10 dark:text-seha-300">
                                {initialsOf(m.member?.fullname)}
                            </div>
                            <div className="min-w-0">
                                <p className="text-sm font-bold text-neutral-800 truncate dark:text-neutral-100">{m.member?.fullname || 'Unknown'}</p>
                                <p className="text-xs text-neutral-500 truncate dark:text-neutral-500">
                                    {ROLE_LABELS[m.member?.role?.role_name] || m.member?.role?.role_name || 'N/A'}
                                    {m.team?.name && <> &middot; {m.team.name}</>}
                                </p>
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-1.5">
                            <button
                                type="button"
                                onClick={() => openRoles(m)}
                                className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                                    m.roles?.length > 0
                                        ? 'bg-seha-50 border-seha-200 text-seha-700 dark:bg-seha-500/10 dark:text-seha-300 dark:border-seha-500/20'
                                        : 'bg-neutral-50 border-neutral-200 text-neutral-500 hover:border-neutral-300 dark:bg-ink-800 dark:border-neutral-700 dark:text-neutral-500 dark:hover:border-neutral-600'
                                }`}
                            >
                                <Tag className="w-3 h-3" />
                                {m.roles?.length > 0 ? m.roles.join(', ') : 'Assign roles'}
                            </button>

                            {arrived ? (
                                <>
                                    <Badge variant="neutral">
                                        <Clock className="w-3 h-3" /> Arrived {new Date(m.ata).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </Badge>
                                    <div className="inline-flex rounded-full border border-neutral-200 overflow-hidden dark:border-neutral-700">
                                        <button
                                            type="button"
                                            disabled={busy}
                                            onClick={() => setStatus(m, 'Active')}
                                            className={`px-2.5 py-1 text-[11px] font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                                                isActive ? 'bg-green-500 text-white' : 'bg-white text-neutral-500 hover:bg-neutral-50 dark:bg-ink-900 dark:text-neutral-400 dark:hover:bg-ink-800'
                                            }`}
                                        >
                                            Active
                                        </button>
                                        <button
                                            type="button"
                                            disabled={busy}
                                            onClick={() => setStatus(m, 'Standby')}
                                            className={`px-2.5 py-1 text-[11px] font-semibold border-l border-neutral-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed dark:border-neutral-700 ${
                                                !isActive ? 'bg-amber-500 text-white' : 'bg-white text-neutral-500 hover:bg-neutral-50 dark:bg-ink-900 dark:text-neutral-400 dark:hover:bg-ink-800'
                                            }`}
                                        >
                                            Standby
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <button
                                    type="button"
                                    disabled={busy}
                                    onClick={() => markArrived(m)}
                                    className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white text-neutral-500 hover:border-seha-300 hover:text-seha-700 px-2.5 py-1 text-[11px] font-semibold disabled:opacity-40 disabled:cursor-not-allowed dark:border-neutral-700 dark:bg-ink-900 dark:text-neutral-500"
                                >
                                    <Clock className="w-3 h-3" /> Mark Arrived
                                </button>
                            )}
                        </div>
                    </div>
                );
            })}

            <Modal
                open={!!rolesFor}
                onClose={() => setRolesFor(null)}
                title="Assign On-Scene Roles"
                subtitle={rolesFor ? `${rolesFor.member?.fullname || 'Responder'} — can hold more than one role.` : ''}
                confirmLabel="Save"
                onConfirm={saveRoles}
            >
                <CheckboxGroup options={INCIDENT_ROLES} value={pendingRoles} onChange={setPendingRoles} />
            </Modal>
        </div>
    );
}

export default ResponderRoster;
